import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Logo } from './Logo';
import {
  FiHome, FiFileText, FiDollarSign, FiCreditCard, FiUsers,
  FiUserCheck, FiLifeBuoy, FiHelpCircle, FiMail, FiBarChart2,
  FiSettings, FiLogOut, FiUser, FiChevronDown, FiMenu, FiX
} from 'react-icons/fi';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: <FiHome size={18} /> },
    { path: '/admin/applications', label: 'Applications', icon: <FiFileText size={18} /> },
    { path: '/admin/loans', label: 'Loans', icon: <FiDollarSign size={18} /> },
    { path: '/admin/payments/pending', label: 'Payments', icon: <FiCreditCard size={18} /> },
    { path: '/admin/customers', label: 'Customers', icon: <FiUsers size={18} /> },
    { path: '/admin/staff', label: 'Staff', icon: <FiUserCheck size={18} /> },
    { path: '/admin/support', label: 'Support', icon: <FiLifeBuoy size={18} /> },
    { path: '/admin/faqs', label: 'FAQs', icon: <FiHelpCircle size={18} /> },
    { path: '/admin/email-templates', label: 'Email Templates', icon: <FiMail size={18} /> },
    { path: '/admin/reports', label: 'Reports', icon: <FiBarChart2 size={18} /> },
    { path: '/admin/settings', label: 'Settings', icon: <FiSettings size={18} /> },
  ];

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '100%',
          margin: '0 auto',
          padding: '0 1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '4rem'
        }}>
          {/* Logo */}
          <Link to="/admin/dashboard" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            textDecoration: 'none',
            marginRight: '2rem'
          }}>
            <Logo size="md" variant="full" />
            <span style={{
              padding: '0.25rem 0.75rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '9999px',
              letterSpacing: '0.025em'
            }}>
              ADMIN
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav style={{
            display: 'flex',
            gap: '0.25rem',
            flex: 1,
            overflowX: 'auto'
          }} className="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.625rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  background: isActive(item.path) ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' : 'transparent',
                  color: isActive(item.path) ? '#1e40af' : '#6b7280'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.color = '#374151';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div style={{ position: 'relative', marginLeft: '1rem' }}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.5rem 1rem',
                background: showUserMenu ? '#f9fafb' : 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#374151'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
              onMouseLeave={(e) => e.currentTarget.style.background = showUserMenu ? '#f9fafb' : 'white'}
            >
              <div style={{
                width: '2rem',
                height: '2rem',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.875rem',
                fontWeight: 600
              }}>
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <span className="desktop-only">{user?.first_name} {user?.last_name}</span>
              <FiChevronDown size={16} style={{
                transition: 'transform 0.2s',
                transform: showUserMenu ? 'rotate(180deg)' : 'rotate(0deg)'
              }} />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div style={{
                position: 'absolute',
                right: 0,
                top: 'calc(100% + 0.5rem)',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.75rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                minWidth: '200px',
                zIndex: 50,
                padding: '0.5rem'
              }}>
                <div style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #f3f4f6',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.125rem' }}>
                    {user?.email}
                  </div>
                </div>
                <Link
                  to="/admin/settings?tab=profile"
                  onClick={() => setShowUserMenu(false)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.625rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#374151',
                    transition: 'background 0.2s',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <FiUser size={16} />
                  Profile & Password
                </Link>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.625rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#ef4444',
                    transition: 'background 0.2s',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#fee2e2'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <FiLogOut size={16} />
                  Sign Out
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-only"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              marginLeft: '1rem',
              padding: '0.5rem',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280'
            }}
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mobile-only" style={{
            borderTop: '1px solid #e5e7eb',
            padding: '1rem',
            background: 'white'
          }}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    background: isActive(item.path) ? '#dbeafe' : 'transparent',
                    color: isActive(item.path) ? '#1e40af' : '#6b7280'
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ flex: 1, background: '#f9fafb' }}>
        {children}
      </main>

      {/* Footer */}
      <footer style={{
        background: '#1f2937',
        color: '#9ca3af',
        padding: '1.5rem',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            &copy; 2025 Coin MFI. Administrative Portal. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Styles for responsive behavior */}
      <style>{`
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-only {
            display: block !important;
          }
          .desktop-only {
            display: none !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-only {
            display: none !important;
          }
        }
        .desktop-nav::-webkit-scrollbar {
          height: 4px;
        }
        .desktop-nav::-webkit-scrollbar-track {
          background: transparent;
        }
        .desktop-nav::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
};
